import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  beginSession,
  accessLinkToken,
  readAuditeeSession,
  sendAuditeeMessage,
  uploadPortalDocuments,
  type SessionState,
  type UploadOutcome,
} from "../api/client";
import { openStream } from "../api/stream";
import { Acknowledgement } from "../components/Acknowledgement";
import { Callout } from "../components/Callout";
import { TextField } from "../components/TextField";
import { extensionLabel, SessionConsole } from "./SessionConsole";
import { BackIcon } from "../components/Icons";
import { useDictation } from "./dictation";

/**
 * The client's side of the session — screens 52 to 54, 55 to 57 and 67.
 *
 * **One composer and no controls.** The client answers; they never grade and they never
 * intervene. That is not a UI choice — `viewer.can_intervene` comes back false from the
 * server and the routes refuse it besides.
 *
 * **Paused means paused.** Screen 67 is this console with the box disabled and a sentence
 * saying the link is still valid, which is the client's actual next question.
 */
export function AuditeeSession({ onLeave }: { onLeave: () => void }) {
  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [connected, setConnected] = useState(true);
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [refusals, setRefusals] = useState<UploadOutcome[]>([]);
  const picker = useRef<HTMLInputElement | null>(null);
  const textarea = useRef<HTMLTextAreaElement | null>(null);

  // One line by default, growing with what is typed — up to 9 lines, past which the box
  // stops growing and scrolls internally instead of pushing the rest of the console down.
  useEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = "auto";
    const style = window.getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || 19.5;
    const cap = lineHeight * 9 + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
  }, [body]);

  useEffect(() => {
    // Pushed, not polled — M14-2. The next question arrives when it is asked, and a pause
    // arrives as a pause: it writes no message, so the server publishes it explicitly
    // rather than leaving the client to discover the audit stopped by having a reply
    // refused (assertion 14).
    const token = accessLinkToken();
    if (!token) return;
    const stream = openStream<SessionState>({
      path: "/portal/session/stream",
      token,
      onState: setState,
      onConnected: setConnected,
    });
    return () => stream.close();
  }, []);

  // **The session stopped existing under the client.** Only a restart does this — the audit
  // day was rerun for testing — and it is not the same as a session that closed: a closed
  // one has a transcript and a final message, and this one has neither. Leaving them on an
  // empty console with a reply box that the server refuses would read as an audit that
  // broke, so they go back to the portal, which is where a restarted audit puts them.
  useEffect(() => {
    if (state !== null && state.session_id === null) onLeave();
  }, [state, onLeave]);

  // **Nothing else wakes this socket while the client's own documents are syncing.** The
  // stream is push-only (M14-2) and no message, upload or finding is happening yet to
  // trigger a frame — so this is the one place on this screen that still asks rather than
  // waits to be told, for exactly as long as `corpus_sync` says `"syncing"` and no longer.
  useEffect(() => {
    if (state?.corpus_sync !== "syncing") return;
    const poll = window.setInterval(() => {
      void readAuditeeSession().then(setState);
    }, 5000);
    return () => window.clearInterval(poll);
  }, [state?.corpus_sync]);

  // **Text sent alongside a document waits for that document, and sends itself.** Migration
  // 0035 — a batch's own ingestion job is now watched, so `corpus_sync` turns `"syncing"` the
  // moment an upload lands and only clears once retrieval can actually see it. `submit()`
  // below stages the text here instead of sending it in the same breath as the upload; this
  // fires it the instant the wait that matters is over, with no second click to remember.
  const [queuedText, setQueuedText] = useState<string | null>(null);
  useEffect(() => {
    if (queuedText === null || state?.corpus_sync === "syncing") return;
    const text = queuedText;
    setQueuedText(null);
    setSending(true);
    sendAuditeeMessage(text)
      .then(setState)
      .catch((cause) => setError(problem(cause)))
      .finally(() => setSending(false));
  }, [state?.corpus_sync, queuedText]);

  // **Read before the screen is guarded, not after.** These derive from a session that may
  // not have arrived yet, so each one reads through `state?.` — the guard that used to stand
  // here now stands below them, because `locked` is what tells the mic to stand down and a
  // hook cannot be called behind a return.
  const paused = state?.status === "paused";
  const closed = state?.status === "closed";
  // **Clause review has ended and nothing composes a reply here.** `phase` moves to
  // `"closing"` once the queue is exhausted, and the deferred turn (`_compose` in
  // `api/routers/portal.py`) only answers `"opening"` and `"clause_review"` — a message sent
  // after that point would sit in the transcript unanswered rather than refused. The server
  // enforces this too (`must_accept_client_messages`); this is what keeps the box from
  // accepting something the route would then reject.
  const reviewComplete = state?.phase === "closing" && !closed;
  // **The audit's own documents are still being indexed.** Retrieval run before this
  // finishes can come back empty for something the client actually sent — read as "not
  // provided" rather than "not indexed yet" — so the composer waits with the rest of the
  // console exactly as it already does for a pause. This covers both the initial,
  // session-opening ingestion and any batch uploaded since — migration 0035 watches a
  // mid-session job the same way, because every turn retrieves through this same
  // knowledge base and none of them reads a document inline.
  const syncing = state?.corpus_sync === "syncing";

  // **Whose turn it is, read from the record rather than from a timer.** The client has
  // spoken and nothing has come back, so the box is theirs to wait in — not to type a
  // second answer into a question nobody has asked yet. `sending` only covers the moment
  // the request is in flight; the wait that matters is longer than that, because Complao
  // is retrieving and writing.
  //
  // `state.generating` is the server's own register of turns in flight and is the same
  // boolean the auditor's banner reads, so the two screens cannot disagree about whose turn
  // it is. The last-speaker reading is kept beside it and neither is redundant: the flag is
  // authoritative once the turn has started, and the reading covers the moment between the
  // answer being recorded and the deferred turn picking it up.
  //
  // Both clear by themselves. The next message arrives over the socket, the flag drops in a
  // `finally` on the server, and nothing here has to be reset.
  // **Nothing is waiting on Complao while the lead auditor is conducting** — M14a-2. The
  // model has stood down, so a box locked until "the next question arrives" would lock the
  // client out of a conversation with a person who is waiting for them to answer.
  // A document upload is the client's own act but answers nothing — `upload()` never
  // schedules a turn the way `sendAuditeeMessage` does — so it is skipped when reading who
  // spoke last. Counting it would lock this composer with "Complao has the turn" after an
  // unprompted, document-only send that nobody is ever going to reply to.
  const lastSpeaker = [...(state?.transcript ?? [])]
    .reverse()
    .find((message) => message.kind !== "document")?.author;
  const awaitingReply =
    !state?.floor_held && (lastSpeaker === "auditee" || state?.generating === true);
  const locked = paused || closed || reviewComplete || sending || awaitingReply || syncing;

  // **The turn comes back and the caret comes with it.** The box is disabled while Complao
  // has the turn, which takes focus away from it — so a client who has just been asked a
  // question would have to find the composer and click it before they could answer. The
  // focus is restored on the transition rather than on every render: on load it would steal
  // the caret from someone still reading the opening message, and on a phone it would open
  // the keyboard over the question they have not read yet.
  const wasLocked = useRef(locked);
  useEffect(() => {
    if (wasLocked.current && !locked) textarea.current?.focus();
    wasLocked.current = locked;
  }, [locked]);

  // Voice input — the mic in the composer. What it recognises is appended to whatever is
  // already in the box and the client sends it themselves; nothing here speaks for them.
  // The append goes through a functional update because dictation and typing run at the
  // same time — a client correcting the last phrase while still speaking must not lose the
  // correction to the next one landing. `blocked` hands the composer's own locked state
  // down: a pause or Complao taking the turn arrives over the socket, and the mic stands
  // down with the box rather than transcribing into a disabled one.
  const dictation = useDictation({
    blocked: locked,
    onText: (heard) =>
      setBody((current) => (current.trim() ? `${current.replace(/\s+$/, "")} ${heard}` : heard)),
  });

  if (!state) {
    return (
      <div className="auditee-shell">
        <p className="sub">{error ?? "Opening the session…"}</p>
      </div>
    );
  }

  // A socket that dropped is a screen that has stopped moving, and on a live audit the
  // client must never mistake that for nothing happening.
  const reconnecting = connected ? null : (
    <p className="sub" role="status">
      Reconnecting to the audit&hellip; your answers are safe and the link still works.
    </p>
  );

  const addFiles = (chosen: File[]) => {
    if (chosen.length === 0) return;
    setPendingFiles((current) => [...current, ...chosen]);
  };

  const removeFile = (index: number) =>
    setPendingFiles((current) => current.filter((_file, position) => position !== index));

  // Files first, then any text — two separate turns rather than one combined bubble, so a
  // sent document reads as its own record the way a reply does. Uploading stays available
  // regardless of whose turn it is (matches the register's own unprompted-upload posture);
  // only the text half respects `locked`.
  const submit = async () => {
    // The mic stops at the moment of sending. A recogniser still running would finalise the
    // tail of a phrase into a box that has just been cleared, so the next answer would open
    // with the end of the last one.
    dictation.stop();
    const text = body.trim();
    const sendText = !locked && text.length > 0;
    const sendFiles = !paused && !reviewComplete && pendingFiles.length > 0;
    if (!sendText && !sendFiles) return;
    setSending(true);
    try {
      let freshState: SessionState | null = null;
      if (sendFiles) {
        const result = await uploadPortalDocuments(pendingFiles);
        const refused = (result.results ?? []).filter((entry) => !entry.accepted);
        setRefusals(refused);
        // Only the accepted files leave the composer — a refused one stays staged with its
        // reason beside it, so the client can remove or replace it rather than lose it.
        const kept = new Set(refused.map((entry) => entry.filename));
        setPendingFiles((current) => current.filter((file) => kept.has(file.name)));
        freshState = await readAuditeeSession();
        setState(freshState);
      }
      if (sendText) {
        if (freshState?.corpus_sync === "syncing") {
          // The documents just sent are still being indexed — this text is not sent to
          // Complao until retrieval can actually see them (the effect above fires it).
          setQueuedText(text);
        } else {
          setState(await sendAuditeeMessage(text));
        }
        setBody("");
      }
      setError(null);
    } catch (cause) {
      setError(problem(cause));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="auditee-shell auditee-shell--console">
      {reconnecting}
      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <SessionConsole
        state={state}
        // **The session is the screen, so the banner is the header.** What stood above the
        // console — the eyebrow, the organisation, the reference line, the status pill and
        // the export — either restated something the banner already carried or belonged on
        // it. The link back to the portal came up from beneath the console for the same
        // reason: a way out of a screen belongs at the top of it, not below a pane the
        // client has to scroll past to find.
        nav={
          <button
            type="button"
            className="linkish"
            title="Back to the audit portal"
            onClick={onLeave}
          >
            <BackIcon size={14} />
            Portal
          </button>
        }
        // Where the day stands, ahead of its name — the same three words the auditor's own
        // console reads, so a client and their auditor cannot describe the session
        // differently to one another over the phone.
        status={
          closed ? (
            <span className="pill p-mute">Audit closed</span>
          ) : paused ? (
            <span className="pill p-wait">Audit paused</span>
          ) : reviewComplete ? (
            <span className="pill p-wait">Under review</span>
          ) : syncing ? (
            <span className="pill p-wait">Preparing documents</span>
          ) : (
            <span className="pill p-ok">
              <span className="livedot" aria-hidden="true" />
              Audit live
            </span>
          )
        }
        // M14a-1 — the client files evidence without leaving the audit. Only their own
        // console gets this: an auditor uploading into the client's register would put the
        // client's name on evidence they did not send.
        //
        // **Withheld once clause review ends**, the same as the composer: this is the
        // document-request card's own upload button (screen 55's "Send this document"),
        // which calls straight through to `uploadPortalDocuments` with no gate of its
        // own — `undefined` here removes the button (`SessionConsole` renders nothing in
        // its place) rather than leave a control that would 409 when pressed.
        onUpload={
          reviewComplete
            ? undefined
            : async (files, answering) => {
                // `answering` closes the request Complao raised during the session. It is
                // not a statement about what the documents are evidence of — ADR 0025
                // unbound that, and nothing is filed by clause.
                await uploadPortalDocuments(files, answering);
                // The register is part of the session projection, so it is re-read rather
                // than patched — and the auditor's console learns of the file over its own
                // socket.
                setState(await readAuditeeSession());
              }
        }
        // The wait is drawn in the thread rather than described in the composer — the dots
        // cover the whole of it, including the moment between the answer committing and the
        // deferred turn picking it up, which `state.generating` is not yet true for.
        typing={awaitingReply && !paused && !closed}
        // Text held back from `sendAuditeeMessage` while the document it was sent with is
        // still syncing — shown as a local echo beside the document tile rather than left
        // to look like it never sent. See `queuedText` above.
        pendingMessage={queuedText}
        footer={
          closed ? (
            <Callout tone="info" title="The audit session has closed.">
              Your lead auditor drafts the Stage 1 report from the record of this session.
              The formal audit decision comes from {state.certification_body}.
            </Callout>
          ) : (
            <>
              {refusals.length > 0 ? (
                <Callout tone="warn" title="These were not accepted.">
                  <ul style={{ margin: 0, paddingLeft: "18px" }}>
                    {refusals.map((entry) => (
                      <li key={entry.filename}>
                        <b>{entry.filename}</b> — {entry.refused}
                      </li>
                    ))}
                  </ul>
                </Callout>
              ) : null}
              <div className="composer">
                {pendingFiles.length > 0 ? (
                  <div className="composer-attachments">
                    {pendingFiles.map((file, index) => (
                      <AttachmentChip
                        key={`${file.name}-${index}-${file.lastModified}`}
                        file={file}
                        onRemove={() => removeFile(index)}
                      />
                    ))}
                  </div>
                ) : null}
                <div className="composer-row">
                  <input
                    ref={picker}
                    type="file"
                    multiple
                    hidden
                    onChange={(event) => {
                      addFiles(Array.from(event.target.files ?? []));
                      event.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="composer-icon-btn"
                    title="Attach a file"
                    aria-label="Attach a file"
                    disabled={paused || reviewComplete || sending}
                    onClick={() => picker.current?.click()}
                  >
                    <PlusIcon />
                  </button>
                  <textarea
                    ref={textarea}
                    className="composer-input"
                    rows={1}
                    value={body}
                    disabled={locked}
                    placeholder={
                      syncing
                        ? "Preparing the documents you sent — this finishes automatically."
                        : reviewComplete
                          ? "Clause review is complete — nothing can be sent from here."
                          : locked && !paused && !closed
                            ? "Complao has the turn — the next question will appear above."
                            : "Type your response here"
                    }
                    onChange={(event) => setBody(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        void submit();
                      }
                    }}
                  />
                  {/* **Voice input dictates; it never answers.** What is recognised is
                      appended to the box for the client to read back and correct, and Send
                      is still theirs to press — an answer committed on a pause in speech
                      would put a sentence nobody approved into the transcript. Unsupported
                      is a stated refusal rather than a hidden control: a client in Firefox
                      pressing a mic that does nothing reads as a platform that is broken. */}
                  <button
                    type="button"
                    className={
                      dictation.recording
                        ? "composer-icon-btn recording"
                        : "composer-icon-btn"
                    }
                    title={
                      !dictation.supported
                        ? "Voice input is not available in this browser — Chrome, Edge or Safari support it"
                        : dictation.recording
                          ? "Stop the microphone"
                          : "Answer by voice — the text appears in the box for you to send"
                    }
                    aria-label={
                      dictation.recording ? "Stop the microphone" : "Answer by voice"
                    }
                    aria-pressed={dictation.recording}
                    disabled={!dictation.supported || locked}
                    onClick={dictation.toggle}
                  >
                    <MicIcon />
                  </button>
                  <button
                    type="button"
                    className="composer-send"
                    title="Send"
                    aria-label="Send"
                    disabled={
                      sending ||
                      reviewComplete ||
                      ((locked || !body.trim()) && (paused || pendingFiles.length === 0))
                    }
                    onClick={() => void submit()}
                  >
                    <SendIcon />
                  </button>
                </div>
              </div>
              <div className="crow">
                <span
                  className={
                    locked && !paused && !closed ? "hint waiting" : "hint"
                  }
                >
                  {/* Four states have something to say and the rest have nothing. A pause
                      is the client's own next question — *is the link still good?* — review
                      being complete is the same question in a different shape, a lead
                      auditor taking the floor changes who they are speaking to, and syncing
                      is the one wait with an end the client can be told about. Waiting on
                      Complao's own reply is none of those: the dots in the thread say it,
                      and a sentence repeating it every turn read as an apology for the
                      platform. */}
                  {paused
                    ? `The audit is paused — nothing can be sent until it resumes. Reason: ${
                        state.pause_reason ?? "—"
                      }. Your access link remains valid.`
                    : reviewComplete
                      ? "Clause review has ended and your lead auditor is reviewing everything recorded today. The closing meeting appears here once that is done."
                      : syncing
                        ? "Complao is indexing the documents you sent so it can answer questions against them — this finishes automatically, usually within a few minutes."
                        : state.floor_held
                          ? `${state.floor_held_by ?? "Your lead auditor"} is conducting this part of the audit directly. Answer them here.`
                          : ""}
                </span>
                {/* **What the microphone is doing, and what it has heard but not settled.**
                    The interim text is shown and never inserted: it is the recogniser's
                    running guess and it revises itself mid-phrase, so a box rewriting under
                    the client's cursor would be unusable. It reaches the box only once the
                    browser marks it final. A refusal — permission denied, no device — is
                    stated here rather than swallowed, because a mic that quietly does
                    nothing is indistinguishable from one that is listening. */}
                {dictation.problem ? (
                  <span className="hint dictation-problem" role="alert">
                    {dictation.problem}
                  </span>
                ) : dictation.recording ? (
                  <span className="hint dictating" role="status">
                    <span className="livedot" aria-hidden="true" />
                    {dictation.interim || "Listening — press the mic again to stop."}
                  </span>
                ) : null}
              </div>
            </>
          )
        }
      />
    </div>
  );
}

/**
 * Screen 52 — the attendance record, and the ICT confirmation in the client's own name.
 *
 * Cl. 9.4.2 opens with participants and roles, so everyone attending is named here and a
 * participant without a role is refused rather than recorded blank. The tick is Cl. 4.1.2
 * again, confirmed at the moment of beginning: screen 45 collected it days earlier, and an
 * agreement to a remote audit made a week ago is not the same statement as one made as the
 * session opens.
 */
export function BeginSessionDialog({
  contactName,
  contactRole,
  joining = false,
  onCancel,
  onOpened,
}: {
  contactName: string;
  contactRole: string;
  //: True when the lead auditor already opened this session from their own console and
  //: Complao is conducting it — this dialog now records the client's own attendance and
  //: their own Cl. 4.1.2 confirmation onto a session already running, rather than creating
  //: one. Same fields, same request; only the wording changes, because the server tells the
  //: two cases apart on its own.
  joining?: boolean;
  onCancel: () => void;
  onOpened: (state: SessionState) => void;
}) {
  const [people, setPeople] = useState([
    { name: contactName, role: contactRole },
    { name: "", role: "" },
  ]);
  const [consent, setConsent] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (index: number, field: "name" | "role", value: string) =>
    setPeople((current) =>
      current.map((person, position) =>
        position === index ? { ...person, [field]: value } : person,
      ),
    );

  const named = people.filter((person) => person.name.trim() || person.role.trim());
  const complete =
    named.length > 0 && named.every((person) => person.name.trim() && person.role.trim());

  const open = async () => {
    setBusy(true);
    try {
      onOpened(
        await beginSession(
          named.map((person) => ({
            name: person.name.trim(),
            role: person.role.trim(),
          })),
          consent,
        ),
      );
    } catch (cause) {
      setError(problem(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true">
        <h2>{joining ? "Join the audit session" : "Begin the audit session"}</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          {joining
            ? "Your lead auditor has already opened this session and Complao is conducting it. Everyone attending is named in the attendance record for the opening meeting — add anyone joining you, because the record has to account for who was present even where their answers are not audit evidence."
            : "Everyone attending is named in the attendance record for the opening meeting. Add anyone joining you — a guide or an observer is named too, because the record has to account for who was present even where their answers are not audit evidence."}
        </p>

        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : null}

        {people.map((person, index) => (
          <div className="grid2" key={index}>
            <TextField
              id={`participant-name-${index}`}
              label={index === 0 ? "Your name" : "Also attending"}
              value={person.name}
              onChange={(value) => set(index, "name", value)}
              required={index === 0}
            />
            <TextField
              id={`participant-role-${index}`}
              label={index === 0 ? "Your role" : "Their role"}
              value={person.role}
              onChange={(value) => set(index, "role", value)}
              required={index === 0}
            />
          </div>
        ))}

        <Acknowledgement
          id="session-ict"
          title="ICT confirmation — IAF MD 4:2025 Cl. 4.1.2."
          checked={consent}
          onChange={setConsent}
        >
          We have the connectivity and access needed to take part, and we agree to this
          audit being conducted using this platform.
        </Acknowledgement>

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !complete || !consent}
            onClick={() => void open()}
          >
            {busy
              ? joining
                ? "Joining…"
                : "Opening…"
              : joining
                ? "Join the session"
                : "Open the session"}
          </button>
        </div>
      </div>
    </div>
  );
}

function problem(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : "Something went wrong.";
}

/**
 * A file staged in the composer, not yet sent — an image gets a real thumbnail from the
 * `File` itself (no upload needed to preview it locally), anything else a tile named for
 * its extension. The "×" removes it from the composer only; nothing has been sent yet.
 */
function AttachmentChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith("image/");
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return undefined;
    const url = URL.createObjectURL(file);
    setThumbnail(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <div className="attach-chip">
      <button
        type="button"
        className="attach-chip-remove"
        title="Remove file"
        aria-label={`Remove ${file.name}`}
        onClick={onRemove}
      >
        <CrossIcon />
      </button>
      <span className="attach-chip-thumb">
        {thumbnail ? (
          <img src={thumbnail} alt="" />
        ) : (
          <span className="attach-chip-ext">{extensionLabel(file.name)}</span>
        )}
      </span>
      <span className="attach-chip-name">{file.name}</span>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" />
      <path d="M19 11a7 7 0 0 1-14 0" />
      <path d="M12 19v3" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
